import {
  Box,
  Button,
  FormControl,
  Grid,
  TextField,
  Typography,
  Skeleton,
} from "@mui/material";

import { useEffect, useState } from "react";
import { Loader, showError, showToast } from "../constants";
import useAuth from "../hooks/useAuth";
import {
  useAddFaqsMutation,
  useDeleteFaqsMutation,
  useLazyGetAllFaqsQuery,
  useUpdateFaqsMutation,
} from "../services/main";
import { CommonBody } from "../types/General";
import { generateEncryptedKeyBody } from "../utils/crypto";
import useTranslation from "../hooks/Translation";
import {
  ARABIC_FIELD_CLASS,
  arabicInputProps,
} from "../utils/arabicField";

type FaqItem = {
  _id: string;
  question: string;
  answer: string;
  question_ar: string;
  answer_ar: string;
};

type FaqSnapshot = {
  question: string;
  answer: string;
  question_ar: string;
  answer_ar: string;
};

const normalizeFaq = (item: Partial<FaqItem>): FaqSnapshot => ({
  question: item?.question || "",
  answer: item?.answer || "",
  question_ar: item?.question_ar || "",
  answer_ar: item?.answer_ar || "",
});

const buildOriginalFaqMap = (items: FaqItem[]) =>
  items.reduce<Record<string, FaqSnapshot>>((acc, item) => {
    acc[item._id] = normalizeFaq(item);
    return acc;
  }, {});

const MainFaq = () => {
  const userData = useAuth();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [questionAr, setQuestionAr] = useState("");
  const [answerAr, setAnswerAr] = useState("");
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [originalFaqs, setOriginalFaqs] = useState<Record<string, FaqSnapshot>>({});
  const [getAllFaqs] = useLazyGetAllFaqsQuery();
  const [addFaqsMutation] = useAddFaqsMutation();
  const [deleteFaqs] = useDeleteFaqsMutation();
  const [updateFaqs] = useUpdateFaqsMutation();
  const [removedItemId, setRemovedItemId] = useState("");
  const [savingFaqId, setSavingFaqId] = useState("");
  const [loading, setLoading] = useState(true);
  const [hidePermission, setHidePermission] = useState<any>({
    isAdd: true,
    isDelete: true,
  });
  const translate = useTranslation();

  const getAllFaqsHandler = async () => {
    setLoading(true);
    try {
      const res = await getAllFaqs({}).unwrap();
      if (res?.statusCode === 200) {
        const data = res?.data?.data || [];
        setFaqs(data);
        setOriginalFaqs(buildOriginalFaqMap(data));
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const isFaqChanged = (item: FaqItem) => {
    const original = originalFaqs[item._id];
    if (!original) {
      return false;
    }
    return JSON.stringify(normalizeFaq(item)) !== JSON.stringify(original);
  };

  const isFaqValid = (item: FaqItem) => {
    const normalized = normalizeFaq(item);
    return !!(
      normalized.question &&
      normalized.answer &&
      normalized.question_ar &&
      normalized.answer_ar
    );
  };

  const handleFaqFieldChange = (
    id: string,
    field: keyof FaqSnapshot,
    value: string
  ) => {
    setFaqs((prev) =>
      prev.map((faq) => (faq._id === id ? { ...faq, [field]: value } : faq))
    );
  };

  const handleUpdateFaq = async (item: FaqItem) => {
    const payload = normalizeFaq(item);

    try {
      setSavingFaqId(item._id);
      const encryptedBody = generateEncryptedKeyBody(payload) as CommonBody;
      const res = await updateFaqs({
        faqId: item._id,
        encryptedBody,
      }).unwrap();

      if (res?.statusCode === 200) {
        setOriginalFaqs((prev) => ({
          ...prev,
          [item._id]: payload,
        }));
        showToast(translate?.error?.faqUpd || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || error?.message || "");
    } finally {
      setSavingFaqId("");
    }
  };

  const handleAddFaq = async () => {
    const newFaq = {
      question: question,
      answer: answer,
      question_ar: questionAr,
      answer_ar: answerAr,
    };

    try {
      let encryptedBody = generateEncryptedKeyBody(newFaq) as CommonBody;
      const res = await addFaqsMutation(encryptedBody).unwrap();
      if (res?.statusCode === 200) {
        getAllFaqsHandler();
        showToast(translate?.error?.faqAdd || "");
        setQuestion("");
        setAnswer("");
        setQuestionAr("");
        setAnswerAr("");
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleDeleteFaq = async (id: string) => {
    try {
      const res = await deleteFaqs({ faqId: id }).unwrap();
      if (res?.statusCode === 200) {
        setRemovedItemId(id);
        getAllFaqsHandler();
        showToast(translate?.error?.faqDel || "");
      }
    } catch (error: any) {
      showError(error?.message || "");
    }
  };

  const checkPermission = () => {
    const permissions = userData?.permission;
    if (permissions?.length) {
      let idx = -1;
      idx = permissions?.findIndex(
        (ele: any) =>
          ele?.name === "Manage FAQs" ||
          ele?.name === "Faqs" ||
          ele?.name === "Manage Faqs"
      );

      if (idx != -1) {
        let data = {
          isAdd: permissions[idx]?.isAdd,
          isDelete: permissions[idx]?.isDelete,
        };
        setHidePermission(data);
      } else {
        setHidePermission({ isAdd: false, isDelete: false });
      }
    }
  };

  useEffect(() => {
    getAllFaqsHandler();
  }, []);

  useEffect(() => {
    if (userData && userData?.permission?.length) {
      checkPermission();
    }
  }, [userData]);

  return (
    <Grid container spacing={2}>
      <Loader isLoad={loading} isLinear />
      {loading ? (
        <>
          {[1, 2].map((i) => (
            <Grid key={i} item xs={12}>
              <Box className="faq_box" sx={{ p: 3, mb: 2 }}>
                <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
                <Skeleton
                  variant="rectangular"
                  height={45}
                  sx={{ borderRadius: 1, mb: 2, width: "100%" }}
                />
                <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
                <Skeleton
                  variant="rectangular"
                  height={45}
                  sx={{ borderRadius: 1, mb: 2, width: "100%" }}
                />
                <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
                <Skeleton
                  variant="rectangular"
                  height={45}
                  sx={{ borderRadius: 1, mb: 2, width: "100%" }}
                />
                <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
                <Skeleton
                  variant="rectangular"
                  height={45}
                  sx={{ borderRadius: 1, mb: 2, width: "100%" }}
                />
              </Box>
            </Grid>
          ))}
        </>
      ) : (
        faqs?.map((item: FaqItem) => {
          const hasChanges = isFaqChanged(item);
          const canSave = hasChanges && isFaqValid(item);

          return (
            <Grid key={item._id} item xs={12}>
              <Box className="faq_box">
                <FormControl sx={{ mb: 2, width: "100%" }}>
                  <Typography className="custom_label">
                    {translate?.main?.QuestionEnglish}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="question"
                    variant="outlined"
                    value={item?.question}
                    disabled={!hidePermission?.isAdd}
                    onChange={(event) =>
                      handleFaqFieldChange(
                        item._id,
                        "question",
                        event.target.value
                      )
                    }
                    placeholder={translate?.main?.QuestionEnglish}
                  ></TextField>
                  <Typography className="custom_label" sx={{ pt: 2 }}>
                    {translate?.main?.QuestionArabic}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="question_ar"
                    variant="outlined"
                    disabled={!hidePermission?.isAdd}
                    className={ARABIC_FIELD_CLASS}
                    inputProps={arabicInputProps}
                    value={item?.question_ar}
                    onChange={(event) =>
                      handleFaqFieldChange(
                        item._id,
                        "question_ar",
                        event.target.value
                      )
                    }
                    placeholder={translate?.main?.QuestionArabic}
                  ></TextField>
                </FormControl>
                <FormControl sx={{ width: "100%" }}>
                  <Typography className="custom_label">
                    {translate?.main?.AnswerEnglish}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="answer"
                    disabled={!hidePermission?.isAdd}
                    variant="outlined"
                    value={item?.answer}
                    onChange={(event) =>
                      handleFaqFieldChange(
                        item._id,
                        "answer",
                        event.target.value
                      )
                    }
                    placeholder={translate?.main?.AnswerEnglish}
                  ></TextField>
                  <Typography className="custom_label" sx={{ pt: 2 }}>
                    {translate?.main?.AnswerArabic}
                  </Typography>
                  <TextField
                    hiddenLabel
                    type={"text"}
                    name="answer_ar"
                    disabled={!hidePermission?.isAdd}
                    variant="outlined"
                    className={ARABIC_FIELD_CLASS}
                    inputProps={arabicInputProps}
                    value={item?.answer_ar}
                    onChange={(event) =>
                      handleFaqFieldChange(
                        item._id,
                        "answer_ar",
                        event.target.value
                      )
                    }
                    placeholder={translate?.main?.AnswerArabic}
                  ></TextField>
                </FormControl>
                <Box className="faq_btn" sx={{ gap: 1 }}>
                  {hidePermission?.isAdd ? (
                    <Button
                      variant="contained"
                      className="btn btn_primary sm"
                      onClick={() => handleUpdateFaq(item)}
                      disabled={!canSave || savingFaqId === item._id}
                    >
                      {translate?.main?.Save}
                    </Button>
                  ) : null}
                  {hidePermission?.isDelete ? (
                    <Button
                      variant="contained"
                      className="btn btn_primary sm"
                      onClick={() => handleDeleteFaq(item?._id)}
                      disabled={removedItemId === item?._id}
                    >
                      {translate?.main?.Remove}
                    </Button>
                  ) : null}
                </Box>
              </Box>
            </Grid>
          );
        })
      )}

      <Grid item xs={12}>
        <Box className="faq_box">
          <FormControl sx={{ mb: 2, width: "100%" }}>
            <Typography className="custom_label">
              {translate?.main?.QuestionEnglish}
            </Typography>
            <TextField
              hiddenLabel
              type={"text"}
              name="name"
              variant="outlined"
              value={question}
              disabled={!hidePermission?.isAdd}
              onChange={(val) => setQuestion(val.target.value)}
              placeholder={translate?.main?.QuestionEnglish}
            ></TextField>
            <Typography className="custom_label" sx={{ pt: 2 }}>
              {translate?.main?.QuestionArabic}
            </Typography>
            <TextField
              hiddenLabel
              type={"text"}
              name="name"
              variant="outlined"
              className={ARABIC_FIELD_CLASS}
              inputProps={arabicInputProps}
              disabled={!hidePermission?.isAdd}
              value={questionAr}
              onChange={(val) => setQuestionAr(val.target.value)}
              placeholder={translate?.main?.QuestionArabic}
            ></TextField>
          </FormControl>
          <FormControl sx={{ width: "100%" }}>
            <Typography className="custom_label">
              {translate?.main?.AnswerEnglish}
            </Typography>
            <TextField
              hiddenLabel
              type={"text"}
              name="name"
              variant="outlined"
              disabled={!hidePermission?.isAdd}
              value={answer}
              onChange={(val) => setAnswer(val.target.value)}
              placeholder={translate?.main?.AnswerEnglish}
            ></TextField>
            <Typography className="custom_label" sx={{ pt: 2 }}>
              {translate?.main?.AnswerArabic}
            </Typography>
            <TextField
              hiddenLabel
              type={"text"}
              name="name"
              variant="outlined"
              className={ARABIC_FIELD_CLASS}
              inputProps={arabicInputProps}
              disabled={!hidePermission?.isAdd}
              value={answerAr}
              onChange={(val) => setAnswerAr(val.target.value)}
              placeholder={translate?.main?.AnswerArabic}
            ></TextField>
          </FormControl>
          <Box className="faq_btn">
            {hidePermission?.isAdd ? (
              <Button
                variant="contained"
                disabled={!question || !answer || !questionAr || !answerAr}
                className="btn btn_primary sm"
                onClick={handleAddFaq}
              >
                {translate?.main?.Add}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default MainFaq;
